import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, Bot, Zap, ArrowRight, Zap as Bolt, Sparkles, TrendingUp,
  Newspaper, Clock, Calendar,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { getServiceIcon, getAccent } from "@/lib/serviceIcons";
import AiChatModal from "@/components/app/AiChatModal";
import ManagerChatModal from "@/components/app/ManagerChatModal";

const COMMS = [
  { type: "Opportunity Alert", icon: Bolt, color: "orange", time: "Just now", timeIcon: Clock, title: "3 New Warm Leads in your area", desc: "We've identified local businesses actively searching for your services. Review them in your Leads Portal.", highlight: true },
  { type: "Product Update", icon: Sparkles, color: "blue", time: "2 days ago", timeIcon: Clock, title: "Automated Proposal Generator V2 is Live", desc: "Create stunning, trackable proposals faster with new smart templates and e-signatures." },
  { type: "Business Trends", icon: TrendingUp, color: "green", time: "Last Week", timeIcon: Calendar, title: "Q3 Marketing Trends for Small Businesses", desc: "The latest shifts in consumer behavior and how to leverage AI-driven content tools." },
  { type: "Monthly Newsletter", icon: Newspaper, color: "purple", time: "July 1, 2026", timeIcon: Calendar, title: "DONDRÁE Insights: A New Era of Growth", desc: "Founder's letter, top ad strategies of the month, and business spotlights." },
];

const commColor = {
  orange: { bg: "bg-orange-500/10", text: "text-orange-400", border: "border-orange-500/20" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-400", border: "border-blue-500/20" },
  green: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/20" },
  purple: { bg: "bg-purple-500/10", text: "text-purple-400", border: "border-purple-500/20" },
};

export default function Dashboard() {
  const { profile } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [mgrOpen, setMgrOpen] = useState(false);

  const manager = profile?.manager || { name: "Sarah Jenkins", role: "Growth Strategist", photo: "" };

  useEffect(() => {
    if (profile) api.getDashboard(profile.id).then(setData).catch(() => {});
  }, [profile]);

  const stats = [
    { label: "Active Services", value: data?.active_count ?? 0 },
    { label: "Total Leads", value: data?.total_leads ?? 0 },
    { label: "Pending Tasks", value: data?.pending_tasks ?? 0 },
  ];

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10 pb-24 relative hide-scrollbar">
      <div className="max-w-6xl mx-auto space-y-8 fade-in relative z-10">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight font-display">Welcome back, {profile?.contact_name?.split(" ")[0]}</h2>
          <p className="text-sm text-zinc-400 mt-1">Here is a high-level overview of your active tools and services.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="glass-panel p-6 rounded-2xl">
              <div className="text-zinc-400 text-xs font-bold uppercase tracking-wider mb-2">{s.label}</div>
              <div className="text-4xl font-bold text-white">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Support team */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button onClick={() => setMgrOpen(true)} className="text-left glass-panel p-6 rounded-2xl flex items-center gap-5 relative overflow-hidden group border border-white/5 hover:border-blue-500/30 transition-colors">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="w-16 h-16 rounded-full bg-blue-900/50 border border-blue-500/50 overflow-hidden flex-shrink-0 flex items-center justify-center text-blue-300 font-bold text-xl">
              {manager.photo ? <img src={manager.photo} alt={manager.name} className="w-full h-full object-cover" /> : manager.name[0]}
            </div>
            <div className="flex-1 z-10">
              <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider mb-1 flex items-center"><span className="w-1.5 h-1.5 rounded-full bg-blue-400 mr-1.5" /> Human Account Manager</div>
              <h4 className="text-white font-bold text-lg mb-0.5">{manager.name}</h4>
              <p className="text-xs text-zinc-400">{manager.role}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 group-hover:text-blue-400 group-hover:bg-blue-500/20 transition-all z-10"><MessageSquare size={16} /></div>
          </button>

          <button onClick={() => setAiOpen(true)} className="text-left glass-panel p-6 rounded-2xl flex items-center gap-5 relative overflow-hidden group border border-white/5 hover:border-purple-500/30 transition-colors">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="w-16 h-16 rounded-full bg-purple-900/30 border border-purple-500/50 flex-shrink-0 flex items-center justify-center text-purple-400 relative">
              <Bot size={26} className="relative z-10" />
              <div className="absolute inset-0 rounded-full border border-purple-400/50 animate-ping opacity-20" />
            </div>
            <div className="flex-1 z-10">
              <div className="text-[10px] font-bold text-purple-400 uppercase tracking-wider mb-1 flex items-center"><span className="w-1.5 h-1.5 rounded-full bg-purple-400 mr-1.5 animate-pulse" /> Dedicated AI Agent</div>
              <h4 className="text-white font-bold text-lg mb-0.5">DONDRÁE Core</h4>
              <p className="text-xs text-zinc-400">24/7 Instant Assistance</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-zinc-400 group-hover:text-purple-400 group-hover:bg-purple-500/20 transition-all z-10"><Zap size={16} /></div>
          </button>
        </div>

        {/* Active tools */}
        <div className="glass-panel rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10 bg-white/5 flex justify-between items-center">
            <h3 className="font-bold text-white">My Active Tools</h3>
            <button onClick={() => navigate("/app/directory")} className="text-xs font-bold text-primary-400 hover:text-primary-300 flex items-center">Browse Directory <ArrowRight size={14} className="ml-1" /></button>
          </div>
          <div className="divide-y divide-white/5 bg-black/20">
            {(data?.active_services || []).length === 0 && (
              <div className="px-6 py-10 text-center text-zinc-500 text-sm">No active tools yet. Activate a service from the directory.</div>
            )}
            {(data?.active_services || []).map((s) => {
              const Icon = getServiceIcon(s.icon);
              const accent = getAccent(s.accent);
              return (
                <div key={s.slug} onClick={() => navigate(`/app/service/${s.slug}`)} className="px-6 py-4 flex items-center gap-4 hover:bg-white/5 transition-colors cursor-pointer">
                  <div className={`w-11 h-11 rounded-xl border flex items-center justify-center ${accent.bg} ${accent.text} ${accent.border}`}><Icon size={20} /></div>
                  <div className="flex-1"><div className="text-white font-bold text-sm">{s.title}</div><div className="text-xs text-zinc-500">{s.badge}</div></div>
                  <ArrowRight size={16} className="text-zinc-600" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Communications & Alerts (moved from tab) */}
        <div className="glass-panel rounded-2xl overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10 bg-white/5">
            <h3 className="font-bold text-white">Communications &amp; Alerts</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Newsletters, product updates, trends & live opportunities.</p>
          </div>
          <div className="divide-y divide-white/5 bg-black/20">
            {COMMS.map((it) => {
              const Icon = it.icon; const TimeIcon = it.timeIcon; const c = commColor[it.color];
              return (
                <div key={it.title} className={`px-6 py-4 flex gap-4 items-center relative group hover:bg-white/5 transition-colors ${it.highlight ? "bg-orange-500/5" : ""}`}>
                  {it.highlight && <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-500" />}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${c.bg} ${c.text} ${c.border}`}><Icon size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${c.bg} ${c.text} ${c.border}`}>{it.type}</span>
                      <span className="text-[11px] text-zinc-500 flex items-center"><TimeIcon size={10} className="mr-1" /> {it.time}</span>
                    </div>
                    <h4 className="text-white font-bold text-sm truncate">{it.title}</h4>
                    <p className="text-xs text-zinc-400 truncate">{it.desc}</p>
                  </div>
                  {it.highlight && <button onClick={() => navigate("/app/leads")} className="hidden md:block px-3 py-1.5 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-white border border-orange-500/20 rounded-lg text-xs font-bold transition-colors flex-shrink-0">View Leads</button>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AiChatModal open={aiOpen} onClose={() => setAiOpen(false)} />
      <ManagerChatModal open={mgrOpen} onClose={() => setMgrOpen(false)} />
    </div>
  );
}
